// Shapes returned by the relay.ledger_* functions and tables (Glorify-LLC/relay#181).
// Hand-written until src/types/database.ts is regenerated after the forgehq
// migration is live (Task 14); keep them in step with the plan's Interfaces.

export type LedgerAccountType = "asset" | "liability" | "equity" | "revenue" | "expense";

export type LedgerSubtype =
  | "bank" | "accounts_receivable" | "other_current_asset" | "fixed_asset" | "other_asset"
  | "accounts_payable" | "credit_card" | "other_current_liability" | "long_term_liability"
  | "equity"
  | "income" | "other_income"
  | "cost_of_goods_sold" | "expense" | "other_expense";

export type LedgerSystemKey =
  | "ar_control" | "ap_control" | "retained_earnings"
  | "opening_balance_equity" | "processor_clearing" | "processor_fees"
  | "class_balance";

/** Lines with no class (spec §2.3a). Used as a class filter value in URLs and the API layer. */
export const UNASSIGNED = "unassigned";
/** A class id, UNASSIGNED, or null for the whole book. */
export type ClassFilter = string | null;

export type EntryStatus = "draft" | "posted" | "voided";
export type EntryKind = "manual" | "opening_balance" | "reversal" | "system";

export const SUBTYPE_TYPE: Record<LedgerSubtype, LedgerAccountType> = {
  bank: "asset",
  accounts_receivable: "asset",
  other_current_asset: "asset",
  fixed_asset: "asset",
  other_asset: "asset",
  accounts_payable: "liability",
  credit_card: "liability",
  other_current_liability: "liability",
  long_term_liability: "liability",
  equity: "equity",
  income: "revenue",
  other_income: "revenue",
  cost_of_goods_sold: "expense",
  expense: "expense",
  other_expense: "expense",
};

export const SUBTYPE_LABEL: Record<LedgerSubtype, string> = {
  bank: "Bank",
  accounts_receivable: "Accounts receivable (A/R)",
  other_current_asset: "Other current asset",
  fixed_asset: "Fixed asset",
  other_asset: "Other asset",
  accounts_payable: "Accounts payable (A/P)",
  credit_card: "Credit card",
  other_current_liability: "Other current liability",
  long_term_liability: "Long-term liability",
  equity: "Equity",
  income: "Income",
  other_income: "Other income",
  cost_of_goods_sold: "Cost of goods sold",
  expense: "Expense",
  other_expense: "Other expense",
};

export const ACCOUNT_TYPE_LABEL: Record<LedgerAccountType, string> = {
  asset: "Assets",
  liability: "Liabilities",
  equity: "Equity",
  revenue: "Income",
  expense: "Expenses",
};

export const SUBTYPES = Object.keys(SUBTYPE_TYPE) as LedgerSubtype[];

export interface LedgerBook {
  id: string;
  organization_id: string;
  name: string;
  currency: string;
  fiscal_year_start_month: number;
  books_start_date: string;
  closed_through: string | null;
  soft_closed_through: string | null;
  solo_operator: boolean;
  has_hard_close: boolean;
  opening_balance_entry_id: string | null;
}

/** relay.ledger_book_for_org(): null when the org has no books. */
export interface BookAssignment {
  book_id: string;
  class_id: string | null;
  is_books_org: boolean;
  can_read: boolean;
  can_write: boolean;
  book: LedgerBook | null;
}

export interface LedgerAccount {
  id: string;
  book_id: string;
  code: string;
  name: string;
  account_type: LedgerAccountType;
  subtype: LedgerSubtype;
  parent_account_id: string | null;
  is_contra: boolean;
  is_active: boolean;
  system_key: LedgerSystemKey | null;
  description: string | null;
}

export interface LedgerClass {
  id: string;
  book_id: string;
  name: string;
  organization_id: string | null;
  is_active: boolean;
}

export interface DraftLine {
  account_id: string;
  class_id: string | null;
  description: string;
  debit_cents: number;
  credit_cents: number;
}

export interface EntryPayload {
  id?: string;
  entry_date: string;
  memo?: string;
  reference?: string;
  lines: DraftLine[];
}

export interface LedgerEntry {
  id: string;
  book_id: string;
  entry_number: number | null;
  entry_date: string;
  status: EntryStatus;
  kind: EntryKind;
  memo: string | null;
  reference: string | null;
  reverses_entry_id: string | null;
  posted_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
  created_at: string;
}

export interface LedgerLine {
  id: string;
  entry_id: string;
  line_no: number;
  account_id: string;
  class_id: string | null;
  description: string | null;
  debit_cents: number;
  credit_cents: number;
}

export interface JournalRow {
  id: string;
  entry_number: number | null;
  entry_date: string;
  status: EntryStatus;
  kind: EntryKind;
  memo: string | null;
  reference: string | null;
  total_cents: number;
  line_count: number;
  reverses_entry_id: string | null;
  reversed_by_entry_id: string | null;
  posted_at: string | null;
  created_at: string;
}

export interface JournalPage {
  total: number;
  entries: JournalRow[];
}

export interface JournalFilters {
  status?: EntryStatus;
  kind?: EntryKind;
  from?: string;
  to?: string;
  account_id?: string;
  class_id?: string;
  q?: string;
}

export interface TrialBalanceRow {
  account_id: string;
  code: string;
  name: string;
  account_type: LedgerAccountType;
  subtype: LedgerSubtype;
  is_contra: boolean;
  system_key: LedgerSystemKey | null;
  debit_cents: number;
  credit_cents: number;
}

/** One row of relay.ledger_trial_balance_by_class: an account's balance within one class (null: Unassigned). */
export interface ClassTrialBalanceRow extends TrialBalanceRow {
  class_id: string | null;
  class_name: string | null;
}

export interface AccountLedgerRow {
  line_id: string;
  entry_id: string;
  entry_number: number;
  entry_date: string;
  status: EntryStatus;
  kind: EntryKind;
  memo: string | null;
  reference: string | null;
  description: string | null;
  class_id: string | null;
  debit_cents: number;
  credit_cents: number;
  balance_cents: number;
}

export interface AccountLedger {
  account: {
    id: string;
    code: string;
    name: string;
    account_type: LedgerAccountType;
    is_contra: boolean;
    system_key: LedgerSystemKey | null;
  };
  from: string;
  to: string;
  opening_cents: number;
  debit_total_cents: number;
  credit_total_cents: number;
  closing_cents: number;
  row_count: number;
  truncated: boolean;
  rows: AccountLedgerRow[];
}

export interface CloseEvent {
  id: string;
  book_id: string;
  kind: "hard_close" | "soft_close" | "reopen" | "override";
  before_date: string | null;
  after_date: string | null;
  reason: string | null;
  entry_id: string | null;
  actor: string | null;
  created_at: string;
}

export interface ImportAccount {
  code: string;
  name: string;
  subtype: LedgerSubtype;
  system_key?: LedgerSystemKey;
  parent_code?: string;
  is_contra?: boolean;
  description?: string;
}

export interface ImportResult {
  created: number;
  updated: number;
  skipped: { code: string | null; reason: string }[];
}

export interface AccountInput {
  id?: string;
  code: string;
  name: string;
  subtype: LedgerSubtype;
  parent_account_id?: string | null;
  is_contra?: boolean;
  description?: string | null;
}

export interface ClassInput {
  id?: string;
  name: string;
  organization_id?: string | null;
  is_active?: boolean;
}
